<template>
  <div class="flex h-full flex-col">
    <p class="mb-4 text-sm leading-relaxed text-gray-600 sm:mb-6 sm:text-base">
      Revisa los cambios antes de confirmar.
    </p>
    <div class="flex max-h-[350px] flex-1 flex-col space-y-6 overflow-y-auto sm:space-y-8 md:max-h-[450px]">
      <div v-if="Object.keys(modifiedProducts).length > 0">
        <h4 class="mb-1 text-sm font-semibold text-gray-600 sm:text-base">Productos modificados:</h4>
        <div class="space-y-1.5 sm:space-y-2">
          <div
            v-for="(modification, productKey) in modifiedProducts"
            :key="productKey"
            class="flex flex-col space-y-2 rounded-lg border border-blue-200 bg-blue-50/50 p-2 transition-all sm:flex-row sm:items-center sm:justify-between sm:space-y-0 sm:p-3"
          >
            <div class="flex items-center space-x-2 sm:space-x-3">
              <img
                :src="modification.subOrderProduct.mainImageUrl"
                class="size-8 rounded-lg object-cover shadow-sm sm:size-9"
              />
              <div class="min-w-0 flex-1">
                <div class="truncate text-xs font-semibold text-gray-900 sm:text-sm">
                  {{ modification.subOrderProduct.productName }}
                </div>
                <div class="text-xs text-gray-500">ID: {{ modification.subOrderProduct.productId }}</div>
              </div>
            </div>
            <div class="rounded-lg bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 sm:px-3 sm:text-sm">
              Cantidad: {{ modification.originalQuantity }} → {{ modification.newQuantity }}
            </div>
          </div>
        </div>
      </div>
      <div v-if="Object.keys(removedProducts).length > 0">
        <h4 class="mb-1 text-sm font-semibold text-gray-600 sm:text-base">Productos sin stock:</h4>
        <div class="space-y-1.5 sm:space-y-2">
          <div
            v-for="(modification, productKey) in removedProducts"
            :key="productKey"
            class="flex flex-col space-y-2 rounded-lg border border-red-200 bg-red-50/50 p-2 transition-all sm:flex-row sm:items-center sm:justify-between sm:space-y-0 sm:p-3"
          >
            <div class="flex items-center space-x-2 sm:space-x-3">
              <img
                :src="modification.subOrderProduct.mainImageUrl"
                class="size-8 rounded-lg object-cover opacity-60 shadow-sm sm:size-9"
              />
              <div class="min-w-0 flex-1">
                <div class="truncate text-xs font-semibold text-gray-500 line-through sm:text-sm">
                  {{ modification.subOrderProduct.productName }}
                </div>
                <div class="text-xs text-gray-400 line-through">ID: {{ modification.subOrderProduct.productId }}</div>
                <div
                  v-if="modification.replacementTime"
                  class="mt-0.5 text-xs text-gray-600"
                >
                  Reposición: {{ modification.replacementTime }}
                </div>
              </div>
            </div>
            <div class="flex items-center justify-center rounded-lg bg-red-100 p-1.5">
              <XMarkIcon class="size-3 text-red-600 sm:size-4" />
            </div>
          </div>
        </div>
      </div>
      <div v-if="selectedProducts.length > 0">
        <h4 class="mb-1 text-sm font-semibold text-gray-600 sm:text-base">Productos recomendados:</h4>
        <div class="space-y-1.5 sm:space-y-2">
          <div
            v-for="product in selectedProducts"
            :key="product.id"
            class="flex flex-col space-y-2 rounded-lg border border-green-200 bg-green-50/50 p-2 transition-all sm:flex-row sm:items-center sm:justify-between sm:space-y-0 sm:p-3"
          >
            <div class="flex items-center space-x-2 sm:space-x-3">
              <img
                :src="product.images?.[0]?.derivatives?.jpgSm?.url"
                class="size-8 rounded-lg object-cover shadow-sm sm:size-9"
              />
              <div class="min-w-0 flex-1">
                <div class="truncate text-xs font-semibold text-gray-900 sm:text-sm">{{ product.name }}</div>
                <div class="text-xs text-gray-500">ID: {{ product.id }}</div>
              </div>
            </div>
            <div class="flex flex-col items-end space-y-0.5 sm:flex-row sm:items-center sm:space-x-2 sm:space-y-0">
              <span class="text-xs font-medium text-gray-700 sm:text-sm"
                >Cantidad: {{ selectedProductQuantities[product.id] || 0 }}</span
              >
              <span class="rounded-full bg-green-200 px-2 py-0.5 text-xs font-medium text-green-800">Nuevo</span>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="!hasChanges"
        class="rounded-lg border border-yellow-200 bg-yellow-50/50 p-4 text-center sm:p-4"
      >
        <div class="text-sm text-yellow-700 sm:text-base">No se han realizado cambios en el pedido.</div>
      </div>
    </div>
    <div class="flex shrink-0 flex-col space-y-2 border-t border-gray-100 pt-3 sm:space-y-3">
      <lokal-loading
        v-if="isLoading"
        :loading="isLoading"
        class="self-center"
      />
      <template v-else>
        <button
          type="button"
          class="w-full rounded-lg bg-lk-green px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-lk-green/90 sm:px-4 sm:py-2.5"
          :disabled="!hasChanges"
          :class="{ 'cursor-not-allowed opacity-50': !hasChanges }"
          @click="emit('confirm-changes')"
        >
          Confirmar propuesta
        </button>
        <button
          type="button"
          class="w-full text-sm text-gray-600 underline transition-colors hover:text-gray-800"
          @click="emit('previous-step')"
        >
          Atrás
        </button>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import lokalLoading from './shared/lokal-loading.vue';

interface SubOrderModificationsSummaryProps {
  productModificationsRecords: Record<number, ProductModification>;
  selectedProducts: Product[];
  selectedProductQuantities: Record<string, number>;
  isLoading: boolean;
}

interface SubOrderModificationsSummaryEmits {
  'previous-step': [];
  'confirm-changes': [];
}

const props = defineProps<SubOrderModificationsSummaryProps>();
const emit = defineEmits<SubOrderModificationsSummaryEmits>();

const modifiedProducts = computed(() => {
  const filteredProductModifications: Record<number, ProductModification> = {};
  Object.entries(props.productModificationsRecords).forEach(([key, productModification]) => {
    if (
      productModification.newQuantity > 0 &&
      productModification.newQuantity !== productModification.originalQuantity
    ) {
      filteredProductModifications[Number(key)] = productModification;
    }
  });
  return filteredProductModifications;
});

const removedProducts = computed(() => {
  const filteredProductModifications: Record<number, ProductModification> = {};
  Object.entries(props.productModificationsRecords).forEach(([key, productModification]) => {
    if (productModification.newQuantity === 0) {
      filteredProductModifications[Number(key)] = productModification;
    }
  });
  return filteredProductModifications;
});

const hasChanges = computed(
  () =>
    Object.keys(modifiedProducts.value).length > 0 ||
    Object.keys(removedProducts.value).length > 0 ||
    props.selectedProducts.length > 0,
);
</script>
